import React, { useState, useCallback } from 'react';
import Button from '@atlaskit/button';
import LoadingButton from '@atlaskit/button/loading-button';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { patchInvoiceGroup } from '@atlassian/temp-proxies';
import { useNotification, successNotificationBuilder, NotificationTypes, errorNotificationBuilder } from '@css-xp/base';
import { failureMessage } from '@css-xp/ppc-common';
import { AnalyticsTrackAction, AnalyticsTrackSubject, sendAnalyticsEvent } from '@atlassian/temp-analytics';

interface DeactivateConfirmModalProps {
  invoiceGroupId: string;
  transactionAccountId: string;
  isDesignIteration1Enabled?: boolean;
  refetchBillingProfile: () => void;
  onClose: () => void;
}

export const DeactivateConfirmModal = ({
  invoiceGroupId,
  transactionAccountId,
  refetchBillingProfile,
  onClose,
  isDesignIteration1Enabled,
}: DeactivateConfirmModalProps) => {
  const [isDeactivating, setIsDeactivating] = useState(false);
  const { notificationSent, sendSuccessNotification, setNotificationSent, sendErrorNotification } = useNotification();

  const handleDeactivate = useCallback(async () => {
    try {
      setIsDeactivating(true);
      await patchInvoiceGroup(invoiceGroupId, transactionAccountId, { active: false });
      if (!notificationSent) {
        const notificationOptions = successNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Billing profile deactivated')
          .build();
        setIsDeactivating(false);
        sendSuccessNotification(notificationOptions);
        setNotificationSent(true);
      }
      onClose();
      refetchBillingProfile();
      sendAnalyticsEvent(AnalyticsTrackAction.DEACTIVATION_SUCCESS, AnalyticsTrackSubject.BILLING_PROFILE, {
        transactionAccountId,
        invoiceGroupId,
      });
    } catch (err) {
      setIsDeactivating(false);
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader("We couldn't deactivate the billing profile")
          .withMessage(failureMessage())
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
      }
      sendAnalyticsEvent(AnalyticsTrackAction.DEACTIVATION_FAILED, AnalyticsTrackSubject.BILLING_PROFILE, {
        transactionAccountId,
        invoiceGroupId,
      });
    }
  }, [
    invoiceGroupId,
    notificationSent,
    onClose,
    refetchBillingProfile,
    sendErrorNotification,
    sendSuccessNotification,
    setNotificationSent,
    transactionAccountId,
  ]);

  const modalAppearance = isDesignIteration1Enabled ? 'warning' : 'danger';
  const modalTitle = isDesignIteration1Enabled
    ? 'Deactivate billing profile'
    : 'You’re about to deactivate this billing profile';

  const modalBody = isDesignIteration1Enabled
    ? 'This action can’t be reversed, and you won’t be able to use this billing profile for any future transactions.'
    : `If you deactivate this billing profile, you will not be able to use it for any future transaction. Are you sure you want to proceed?`;
  return (
    <ModalTransition>
      <Modal onClose={onClose} testId="deactivate-confirm-modal" width={isDesignIteration1Enabled ? 'small' : 'medium'}>
        <ModalHeader>
          <ModalTitle appearance={modalAppearance}>{modalTitle}</ModalTitle>
        </ModalHeader>
        <ModalBody>{modalBody}</ModalBody>
        <ModalFooter>
          <Button onClick={onClose} testId="cancel-button">
            Cancel
          </Button>
          <LoadingButton
            appearance={isDesignIteration1Enabled ? 'warning' : 'danger'}
            onClick={() => handleDeactivate()}
            isLoading={isDeactivating}
            testId="deactivate-button"
          >
            Deactivate
          </LoadingButton>
        </ModalFooter>
      </Modal>
    </ModalTransition>
  );
};
